import $ from 'jquery';
import { getConfig } from './config';
import { debug, resetFormState, throwError } from './utils';
import { resolveOrderProductId } from './subscribeProduct';
import { useCheckoutGateway, gatewayRequest } from './checkoutGateway';

// Coupon validation.
//
// validateOfferCoupon() reads the coupon code (#cShipCoupon), checks it, decides
// validity (the coupon is either order-wide or restricted to the selected
// product), and dispatches a `konnektive:coupon-applied` DOM event carrying the
// coupon details:
//
//   document.addEventListener('konnektive:coupon-applied', function (e) {
//     var d = e.detail;              // { valid, code, discountType, discountPercent,
//                                     //   discountPrice, productIndex, reason, message }
//     if (!d.valid) { /* show error */ return; }
//     /* apply d.discount* to your voucher row + totals */
//   });
//
// Direct mode (no checkoutGatewayURL, unchanged since 1.5): the check is
// Konnektive's /coupon/query on konnektiveAPIURL, and all display/total work
// stays in the per-funnel scripts.
//
// Gateway mode (1.6, G-8): the check is the gateway's /coupon route
// (lambda-functions#19), and the library draws the result itself (voucher row,
// error, `voucherDiscount`, `#cBillPromoCode`, initTotalBox()) for a site with
// no coupon code of its own. A site that wants its own display calls
// e.preventDefault() in its listener and the library draws nothing. A site
// with its own validateOfferCoupon keeps it: see routeSiteCouponQueries().

// The library draws the coupon result only in gateway mode and only for its own
// check (a site without a coupon function of its own); a 1.5-style direct-mode
// funnel keeps drawing it the way it does today, and a site's own
// validateOfferCoupon is kept (see routeSiteCouponQueries).
function emitCoupon(detail) {
	const event = new CustomEvent('konnektive:coupon-applied', { detail, cancelable: true });
	document.dispatchEvent(event);
	if (useCheckoutGateway() && !event.defaultPrevented) {
		drawCoupon(detail);
	}
	return detail;
}

// A click handler bound as `.click(validateOfferCoupon)` passes the event in;
// only a string is a code.
function readCode(couponCode) {
	// Trimmed: phone autocomplete often adds a trailing space.
	if (typeof couponCode === 'string') {
		return couponCode.trim();
	}
	return String($('#cShipCoupon').val() || '').trim();
}

export function validateOfferCoupon(couponCode) {
	const code = readCode(couponCode);
	if (!code) {
		return emitCoupon({ valid: false, reason: 'empty', code: '' });
	}

	// Coupons apply to one-time purchases only — reject on subscribe plans
	// before hitting the API. The funnel shows getConfig().couponMessage.
	if ($('#cBillPurchaseType').val() === 'subscribe') {
		return emitCoupon({
			valid: false,
			reason: 'subscribe',
			code,
			message: 'This coupon is only valid for one-time purchases.',
		});
	}

	const productIndex = $('#cBillProductIndex').val() || 0;
	const product = getConfig().products[productIndex];
	const productCalculatedID = resolveOrderProductId(product,
		$('#cBillPurchaseType').val(), $('#cBillSubscribeIndex').val());

	$('.overlay, .processing').show();

	if (useCheckoutGateway()) {
		return validateCouponViaGateway(code, productIndex, productCalculatedID);
	}

	const settings = {
		url: `${getConfig().konnektiveAPIURL}/coupon/query/?campaignId=` + product.campaignID_konnektive + `&couponCode=` + code,
		method: 'POST',
		timeout: 0,
		headers: { 'X-SESSION-ID': '' },
	};

	$.ajax(settings).done((response) => {
		debug(response);
		resetFormState();
		if (response.result !== 'SUCCESS') {
			emitCoupon({ valid: false, reason: 'error', code, message: response.message, response });
			return;
		}
		const data = response.message.data[0];
		// Order-wide coupon (no campaignProductId) or restricted to specific
		// products that include the selected one.
		const restricted = !!data.campaignProductId;
		const matches = !restricted || data.campaignProductId.indexOf(productCalculatedID) !== -1;
		if (!matches) {
			emitCoupon({ valid: false, reason: 'product', code, message: response.message, response });
			return;
		}
		emitCoupon({
			valid: true,
			code,
			discountType: data.discountType,        // 'PERCENT' | (absolute otherwise)
			discountPercent: data.couponDiscountPerc,
			discountPrice: data.couponDiscountPrice,
			productIndex,
			response,
		});
	}).fail((jqXHR, textStatus, error) => {
		resetFormState();
		debug('Coupon request failed: ', textStatus, error);
		emitCoupon({ valid: false, reason: 'network', code, message: textStatus });
	});
}

/* ---------------------------------------------------------------- gateway -- */

const COUPON_REFUSED_MESSAGE = 'This coupon cannot be checked right now. Please continue without it or call us.';
const COUPON_LIMIT_MESSAGE = 'Too many coupon attempts. Please continue without a coupon.';
const COUPON_NETWORK_MESSAGE = 'We could not check this coupon. Please try again.';

// The gateway's /coupon (lambda-functions#19) answers in Konnektive's
// /coupon/query shape, trimmed to the fields below:
//   valid:   200 { result: 'SUCCESS', message: { data: [{ campaignProductId,
//            discountType, couponDiscountPerc, couponDiscountPrice }] } }
//   invalid: 200 { result: 'ERROR', message: 'Invalid coupon code' }
// campaignProductId empty = the coupon applies to the whole order; otherwise it
// lists the product ids it is limited to, as in direct mode above.
// Konnektive prefix-matches codes, so one query can return several rows. When
// the rows carry couponCode, take the exact match; otherwise the first row, as
// direct mode does.
function readCouponRow(reply, code) {
	if (!reply || String(reply.result).toUpperCase() !== 'SUCCESS') {
		return null;
	}
	const rows = reply.message && reply.message.data;
	if (!Array.isArray(rows) || !rows.length) {
		return null;
	}
	const wanted = String(code || '').toUpperCase();
	const exact = rows.filter((row) => row && row.couponCode
		&& String(row.couponCode).toUpperCase() === wanted);
	if (exact.length) {
		return exact[0];
	}
	return rows.some((row) => row && row.couponCode) ? null : rows[0];
}

function couponProductIds(raw) {
	if (Array.isArray(raw)) {
		return raw.map(String);
	}
	if (raw === undefined || raw === null || raw === '') {
		return [];
	}
	return String(raw).split(',').map((id) => id.trim()).filter(Boolean);
}

// The gateway checks the code against the session's campaign, which the
// library opens with products[0]'s campaignID_konnektive, and scopes it to
// product1_id (the selected product) as some sites' own coupon code did.
function validateCouponViaGateway(code, productIndex, productCalculatedID) {
	const body = { couponCode: code };
	if (productCalculatedID) {
		body.product1_id = productCalculatedID;
	}
	gatewayRequest('coupon', body)
		.done((reply) => {
			debug(reply);
			resetFormState();
			const row = readCouponRow(reply, code);
			if (!row) {
				const message = reply && typeof reply.message === 'string' ? reply.message : '';
				emitCoupon({ valid: false, reason: 'error', code, message: message || 'This coupon code is not valid.' });
				return;
			}
			const productIds = couponProductIds(row.campaignProductId);
			if (productIds.length && productIds.indexOf(String(productCalculatedID)) === -1) {
				emitCoupon({ valid: false, reason: 'product', code, message: 'This coupon is not valid for the selected product.' });
				return;
			}
			emitCoupon({
				valid: true,
				code,
				discountType: row.discountType,
				discountPercent: row.couponDiscountPerc,
				discountPrice: row.couponDiscountPrice,
				productIndex,
			});
		})
		.fail((err) => {
			// Always clear the spinner, whatever went wrong.
			resetFormState();
			debug((err && err.responseJSON) || err);
			const status = err && err.status;
			if (status === 429) {
				emitCoupon({ valid: false, reason: 'limit', code, message: COUPON_LIMIT_MESSAGE });
			} else if (status === 400) {
				// The gateway refuses a code it cannot be (bad characters, too long).
				emitCoupon({ valid: false, reason: 'error', code, message: refusalMessage(err) });
			} else if (status === 403 || status === 409 || status === 401) {
				// 403 refused / 409 checkout already completed / 401 session expired.
				emitCoupon({ valid: false, reason: 'refused', code, message: COUPON_REFUSED_MESSAGE });
			} else {
				emitCoupon({ valid: false, reason: 'network', code, message: COUPON_NETWORK_MESSAGE });
			}
		});
}

// Default coupon display, modelled on the sites' own validateOfferCoupon()
// (constantfocus, buyaquallure, xytomax): voucher row, Apply button, promo field
// the order sends, the page's `voucherDiscount` global and initTotalBox().
// Every write is an assignment, so drawing twice (or after a site listener that
// did the same) gives the same result.
function drawCoupon(detail) {
	if (!detail.valid) {
		if (detail.reason === 'empty') {
			$('#cShipCoupon_errorSpot').html('Enter coupon code').show();
			return;
		}
		const siteMessage = getConfig().couponMessage;
		if ((detail.reason === 'product' || detail.reason === 'subscribe') && siteMessage) {
			throwError('error', siteMessage);
		} else {
			throwError('error', detail.message || 'This coupon code is not valid.');
		}
		return;
	}

	const product = getConfig().products[detail.productIndex] || {};
	const stdPrice = parseFloat(product.price) || 0;
	// Sites keep the subscribe & save discount in a page global `realDiscount`;
	// the percent applies to the price after it, as their own code did.
	const realDiscount = parseFloat(window.realDiscount) || 0;
	const discount = detail.discountType === 'PERCENT'
		? ((stdPrice - realDiscount) * (parseFloat(detail.discountPercent) || 0)) / 100
		: (parseFloat(detail.discountPrice) || 0);

	// Read by the page's own initTotalBox(). Only reaches a page that declares
	// it with `var` (a window property), as every known funnel does.
	window.voucherDiscount = discount;

	$('#cShipCoupon_errorSpot').html('').hide();
	$('.cpnBtn').text('Applied').addClass('disabled');
	$('.cpnBtn, #cShipCoupon').prop('disabled', true);
	$('#voucherInfoCode').text(detail.code);
	$('#voucherInfoDiscount, #voucherInfoDiscount2').text('-$' + discount.toFixed(2));
	$('#voucherInfo').show();
	// The order sends #cBillPromoCode as couponCode; Konnektive applies the
	// discount from it at /order.
	$('#cBillPromoCode').val(detail.code);
	if (typeof window.initTotalBox === 'function') {
		window.initTotalBox();
	}
}

// Sites keep their own validateOfferCoupon() (many funnels draw per-package
// discounts, banners and ?ccode auto-apply around it). In gateway mode the one
// thing they must not do is call #7's /coupon/query, so that request is
// answered from the gateway's /coupon instead. The gateway replies in
// Konnektive's /coupon/query shape, so the site's own code reads it unchanged.
// A gateway refusal (limit, expired checkout, network) comes back as
// { result: 'ERROR', message } so the site's error branch clears its spinner.
let siteCouponRouteInstalled = false;

// The gateway body for a site request to #7's /coupon/query
// ({ couponCode, product1_id? }), or null when the request is something else.
// product1_id is kept: some funnels (avinolpm) send it so Konnektive limits the
// check to the selected product, and never check campaignProductId themselves.
function siteCouponQuery(options) {
	const url = String((options && options.url) || '');
	const base = String(getConfig().konnektiveAPIURL || '').replace(/\/$/, '');
	if (!base || url.indexOf(base) !== 0 || !/\/coupon\/query\/?(\?|$)/.test(url)) {
		return null;
	}
	const fromUrl = new URLSearchParams(url.split('?')[1] || '');
	let fromData = {};
	if (typeof options.data === 'string') {
		const params = new URLSearchParams(options.data);
		fromData = { couponCode: params.get('couponCode'), product1_id: params.get('product1_id') };
	} else if (options.data && typeof options.data === 'object') {
		fromData = options.data;
	}
	const body = { couponCode: String(fromUrl.get('couponCode') || fromData.couponCode || '') };
	const productId = fromUrl.get('product1_id') || fromData.product1_id;
	if (productId) {
		body.product1_id = String(productId);
	}
	return body;
}

// Site coupon functions send the same request twice per Apply (nested
// $.ajax(settings)). Each gateway check counts against the per-checkout limit,
// so a repeat of the same query within a few seconds reuses the first answer.
const SITE_COUPON_REUSE_MS = 5000;
let lastSiteCoupon = null;

function gatewayCouponFor(body) {
	const key = JSON.stringify(body);
	const now = Date.now();
	if (lastSiteCoupon && lastSiteCoupon.key === key && now - lastSiteCoupon.at < SITE_COUPON_REUSE_MS) {
		return lastSiteCoupon.request;
	}
	const request = gatewayRequest('coupon', body);
	lastSiteCoupon = { key, at: now, request };
	// A failed check is not reused: the shopper is told to try again.
	request.fail(() => {
		if (lastSiteCoupon && lastSiteCoupon.request === request) {
			lastSiteCoupon = null;
		}
	});
	return request;
}

function refusalMessage(err) {
	const status = err && err.status;
	if (status === 429) {
		return COUPON_LIMIT_MESSAGE;
	}
	if (status === 400) {
		return (err.responseJSON && err.responseJSON.message) || 'This coupon code is not valid.';
	}
	if (status === 403 || status === 409 || status === 401) {
		return COUPON_REFUSED_MESSAGE;
	}
	return COUPON_NETWORK_MESSAGE;
}

// Installed once by init; returns true when it installed the route.
export function routeSiteCouponQueries() {
	if (siteCouponRouteInstalled || !useCheckoutGateway()) {
		return false;
	}
	siteCouponRouteInstalled = true;
	$.ajaxTransport('+*', (options) => {
		if (!useCheckoutGateway()) {
			return undefined;
		}
		const body = siteCouponQuery(options);
		if (body === null) {
			return undefined;
		}
		let aborted = false;
		return {
			send(headers, complete) {
				const answer = (body) => {
					if (!aborted) {
						complete(200, 'success', { text: JSON.stringify(body) }, 'Content-Type: application/json\r\n');
					}
				};
				if (!body.couponCode) {
					answer({ result: 'ERROR', message: 'This coupon code is not valid.' });
					return;
				}
				gatewayCouponFor(body)
					.done((reply) => answer(reply))
					.fail((err) => {
						debug((err && err.responseJSON) || err);
						answer({ result: 'ERROR', message: refusalMessage(err) });
					});
			},
			abort() {
				aborted = true;
			},
		};
	});
	return true;
}
